/**
 * @file useKeyboardShortcuts.js
 * @location src/hooks/useKeyboardShortcuts.js
 * @role 전역 키보드 단축키 등록 헬퍼
 *
 * 사용:
 *   useKeyboardShortcuts([
 *     { combo: 'mod+k', handler: () => setSearchOpen(true) },
 *     { combo: 'mod+/', handler: () => setHelpOpen(true) },
 *     { combo: '?',     handler: () => setHelpOpen(true), allowInInput: false },
 *   ]);
 *
 * - `mod` = Ctrl (Win/Linux) or Cmd (Mac) 자동 감지
 * - `shift` / `alt` 도 같은 방식: `'mod+shift+k'`
 * - 기본은 input/textarea/contenteditable 포커스 중에는 무시 (`allowInInput: true` 로 활성화)
 * - handler 는 `e.preventDefault()` 자동 호출 — 브라우저 기본 동작 차단
 */
import { useEffect } from 'react';

function parseCombo(combo) {
  const parts = combo.toLowerCase().split('+').map(s => s.trim());
  return {
    mod:   parts.includes('mod'),
    shift: parts.includes('shift'),
    alt:   parts.includes('alt'),
    key:   parts.filter(p => !['mod', 'shift', 'alt'].includes(p))[0] ?? '',
  };
}

function isInputElement(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

/**
 * @param {Array<{ combo: string, handler: (e: KeyboardEvent) => void, allowInInput?: boolean }>} shortcuts
 */
export function useKeyboardShortcuts(shortcuts) {
  useEffect(() => {
    const parsed = shortcuts.map(s => ({ ...s, ...parseCombo(s.combo) }));
    const onKey = (e) => {
      const inInput = isInputElement(document.activeElement);
      for (const s of parsed) {
        if (inInput && !s.allowInInput) continue;
        const modOk   = s.mod   ? (e.metaKey || e.ctrlKey) : !(e.metaKey || e.ctrlKey);
        const shiftOk = s.shift === e.shiftKey;
        const altOk   = s.alt   === e.altKey;
        const keyOk   = e.key.toLowerCase() === s.key;
        if (modOk && shiftOk && altOk && keyOk) {
          e.preventDefault();
          s.handler(e);
          return;
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // shortcuts 가 매 렌더마다 새 배열이라도 동작 보장 — 가벼운 비교 부담만 (key 가 적음)
  }, [shortcuts]);
}
